import React, { useState, useEffect } from "react";
import Icon from "../../components/ui/icon";
import "../../assets/styles/pages/publishers/settings.css";
import SideNav from "../../components/layout/sideNav";
import { useToast } from "../../context/toastContext";
import { useNavigate } from "react-router-dom";
import api from "../../services/apiClient";
import DOMPurify from "dompurify";
import { storageManager } from "../../utils/storageManager";

const SettingsPage = () => {
  const navigate = useNavigate();
  const showToast = useToast();
  const [isSideNavActive, setisSideNavActive] = useState(false);
  const [userInfo, setUserInfo] = useState(
    JSON.parse(localStorage.getItem("PublisherInfo")) || {}
  );
  const [settings, setSettings] = useState(() => {
    const saved = localStorage.getItem("pubSettings");
    return saved ? JSON.parse(saved) : {};
  });
  const [currency, setCurrency] = useState(
    storageManager.getCurrency()
  );
  const [loading, setLoading] = useState(null);
  const [name, setName] = useState("");
  const [gSaveD, setGSaveD] = useState(false);
  const [isPublisherVerified, setisPublisherVerified] = useState(false);
  const currencyList = ["USD", "EUR", "INR"];
  const [optionWindowActive, setOptionWindowActive] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("aToken");
    if (!token) {
      navigate("/auth/login");
      return;
    }

    if (userInfo) {
      setName(userInfo.name);
    }

    if (isPublisherVerified) return;

    api
      .get(`/api/publisher/info`)
      .then((response) => {
        setisPublisherVerified(true);
        setUserInfo(response.data.data);
        localStorage.setItem(
          "PublisherInfo",
          JSON.stringify(response.data.data)
        );
      })
      .catch((err) => {
        showToast({
          status: "error",
          message: err.response?.data?.message || "Login not found.",
        });
        navigate("/auth/login");
      });
  }, [isPublisherVerified, navigate, showToast, userInfo]);

  const sanitizeInput = (value) => DOMPurify.sanitize(value);

  const handelNameChange = (e) => {
    const value = sanitizeInput(e.target.value);
    setName(value);
  };

  const handelSaveGBtn = async () => {
    if (gSaveD) return;
    setGSaveD(true);

    const sanitizedName = sanitizeInput(name);
    const sanitizedCurrency = sanitizeInput(currency);
    const nameInput = document.getElementById("name");

    const fail = (message) => {
      showToast({ status: "error", message });
      nameInput?.focus();
      setGSaveD(false);
    };

    if (!sanitizedName) {
      return fail("Name is required.");
    }

    if (sanitizedName.length < 3 || sanitizedName.length > 50) {
      return fail("Name must be between 3 and 50 characters.");
    }

    if (!/^[a-zA-Z\s]+$/.test(sanitizedName)) {
      return fail("Name can only contain letters and spaces.");
    }

    if (!sanitizedCurrency) {
      return fail("Currency is required.");
    }

    if (sanitizedCurrency.length !== 3) {
      return fail("Currency must be exactly 3 characters (e.g. INR, USD).");
    }

    try {
      const res = await api.put("/api/publisher/update-general-info", {
        name: sanitizedName,
        currency: sanitizedCurrency,
      });

      showToast({
        status: res.data.status ? "success" : "error",
        message: res.data.message || "Updated successfully.",
      });

      storageManager.setCurrency(sanitizeInput(sanitizedCurrency));
    } catch (err) {
      showToast({
        status: "error",
        message: err.response?.data?.message || "Something went wrong.",
      });
    } finally {
      setGSaveD(false);
    }
  };


  const toggleSetting = async (settingKey) => {
    if (loading) return;

    const newValue = !settings[settingKey];
    setLoading(settingKey);

    try {
      const response = await api.put("/api/publisher/settings", {
        settingKey,
        value: newValue,
      });

      if (response.status === 200) {
        setSettings((prevSettings) => ({
          ...prevSettings,
          [settingKey]: newValue,
        }));
        localStorage.setItem(
          "pubSettings",
          JSON.stringify(response.data.settings)
        );
      }
    } catch (error) {
      showToast({
        status: "error",
        message: error.response?.data?.message || "Unable to Update!",
      });
    } finally {
      setLoading(null);
    }
  };

  return (
    <div className="settings__page">
      <SideNav
        userName={userInfo.name}
        emailID={userInfo.email}
        active={isSideNavActive}
        campaignOpenInital={false}
        accManagerName={userInfo.adminInfo?.name}
        accManagerEmail={userInfo.adminInfo?.email}
      />
      <div
        onClick={() => setisSideNavActive(!isSideNavActive)}
        className={
          isSideNavActive ? "background-shadow active" : "background-shadow"
        }
      ></div>
      <main>
        <header className="desktop-header">
          <div>
            <h2>Settings</h2>
            <p>Mange your account basic controls from here.</p>
          </div>
        </header>

        <header className="mobile-header">
          <div>
            <h2>Settings</h2>
          </div>
          <button
            className="nav-btn"
            onClick={() => setisSideNavActive(!isSideNavActive)}
          >
            {isSideNavActive ? (
              <Icon name="XMark" className="onLeft" />
            ) : (
              <Icon name="Bars3" className="onLeft" />
            )}
          </button>
        </header>

        <div className="main-container">
          <div className="general-container">
            <h2>General Settings</h2>
            <div className="divider"></div>
            <div className="form-container">
              <div className="inp-box">
                <label htmlFor="name">Name</label>
                <input
                  type="text"
                  id="name"
                  value={name}
                  onChange={handelNameChange}
                  placeholder="Enter your name here."
                  autoComplete="off"
                />
              </div>
              <div className="inp-box">
                <label htmlFor="email">Email ID</label>
                <input
                  type="email"
                  id="email"
                  value={userInfo?.email}
                  readOnly
                  placeholder="Enter your email ID here."
                  autoComplete="off"
                />
              </div>
              <div className="inp-box">
                <label htmlFor="mobileNumber">Mobile No</label>
                <input
                  type="text"
                  autoComplete="off"
                  id="mobileNumber"
                  value={userInfo?.phone}
                  readOnly
                  placeholder="+91 9999999999"
                />
              </div>
              <div className="inp-box">
                <label htmlFor="currency">Currency</label>
                <div className="inp-with-icon">
                  <input
                    id="currency"
                    type="text"
                    placeholder="Select Currency"
                    autoComplete="off"
                    value={currency}
                    readOnly
                    onClick={() =>
                      setOptionWindowActive(
                        optionWindowActive === "type" ? null : "type"
                      )
                    }
                  />
                  <Icon name="ChevronDown" />
                </div>

                <div
                  className={`selection-container ${optionWindowActive === "type" ? "active" : ""
                    }`}
                >
                  <div className="notch"></div>
                  {currencyList &&
                    currencyList.length > 0 &&
                    currencyList.map((cur) => {
                      return (
                        <div
                          key={cur}
                          className={`selection-box ${cur === currency ? "active" : ""
                            }`}
                          onClick={() => {
                            setCurrency(sanitizeInput(cur));
                            setOptionWindowActive(false);
                          }}
                        >
                          <span>{cur}</span>
                          <Icon name="Check" />
                        </div>
                      );
                    })}
                </div>
              </div>
              <button disabled={gSaveD} onClick={handelSaveGBtn}>
                {gSaveD ? "Saving" : "Save"}
                {gSaveD ? (
                  <Icon name="ArrowPath" className="spin" />
                ) : (
                  <Icon name="ChevronRight" />
                )}
              </button>
            </div>
          </div>
          <div className="controls-container">
            <h2>Basic Controls</h2>
            <div className="divider"></div>
            <div className="control">
              <div className="text-info">
                <span>Global Postback</span>
                <p>
                  when off if lead come it does not hit your global postback.
                </p>
              </div>
              <div
                className={`toggle-switch ${settings.globalPostback ? "on" : ""
                  } ${loading === "globalPostback" ? "changing" : ""}`}
                onClick={() => toggleSetting("globalPostback")}
              >
                <div className="inner-circle"></div>
              </div>
            </div>
            <div className="control">
              <div className="text-info">
                <span>Meta Integration</span>
                <p>
                  Enable or disable Meta Ads event delivery.
                </p>
              </div>
              <div
                className={`toggle-switch ${settings.metaIntegration ? "on" : ""
                  } ${loading === "metaIntegration" ? "changing" : ""}`}
                onClick={() => toggleSetting("metaIntegration")}
              >
                <div className="inner-circle"></div>
              </div>
            </div>
          </div>
        </div>

        <span className="copyright-text">
          © 2025 Swift Digital Ads, All rights reserved
        </span>
      </main>
    </div>
  );
};

export default SettingsPage;
